import { DASH_META_ROOT } from '@revelio/auth';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

type AiSearchCsrfResponse = {
  csrfToken: string;
};

let cachedAiSearchCsrfToken: string | null = null;

const fetchAiSearchCsrfToken = async (): Promise<string> => {
  const response = await fetch(`${DASH_META_ROOT}/surf/ai-search`, {
    headers: { 'request-id': crypto.randomUUID() },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Failed to fetch AI search CSRF token (${response.status})`
    );
  }

  const { csrfToken }: AiSearchCsrfResponse = await response.json();
  return csrfToken;
};

export const getAiSearchCsrfToken = async (): Promise<string> => {
  if (cachedAiSearchCsrfToken) {
    return cachedAiSearchCsrfToken;
  }

  const csrfToken = await fetchAiSearchCsrfToken();
  cachedAiSearchCsrfToken = csrfToken;
  return csrfToken;
};

export const aiFilterSearchRequest = async ({
  prompt,
  rolesTax,
  skillsTax,
  signal,
}: {
  prompt: string;
  rolesTax: string;
  skillsTax: number;
  signal?: AbortSignal;
}): Promise<{
  response: TalentDiscoveryAiFilterSearchResponseV2;
  uuid: string;
}> => {
  const csrfToken = await getAiSearchCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/ai-search/talent-discovery-v2`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta-ai-search': csrfToken,
      },
      body: JSON.stringify({
        text: prompt,
        roles_tax: rolesTax,
        skills_tax: skillsTax,
      }),
      credentials: 'include',
      signal,
    }
  );
  const data: unknown = await response.json();
  return data as {
    response: TalentDiscoveryAiFilterSearchResponseV2;
    uuid: string;
  };
};
